<template>
  <div id="SectionHero">
    <div class="SectionHero__container">
      <div class="SectionHero__content">
        <SectionTitle titleTag="h1">
          <template #title>
            Ship
            <span class="SectionHero__title-highlight">audit-ready</span>
            ESG & GHG reports
          </template>
        </SectionTitle>

        <ul class="SectionHero__features">
          <li
            v-for="feature in features"
            :key="feature"
            class="SectionHero__feature"
          >
            <UIIcon class="SectionHero__feature-icon" name="checkCircle" />
            {{ feature }}
          </li>
        </ul>

        <div class="SectionHero__cta">
          <UIButton size="xl" :to="{ name: 'Contact' }">Book a demo</UIButton>
        </div>

        <UIRating class="SectionHero__rating" />
      </div>

      <div class="SectionHero__image-container">
        <img
          class="SectionHero__image"
          :src="image"
          alt="Sustainalyse ESG reporting dashboard showing GHG emissions tracking and ESRS compliance modules"
          fetchpriority="high"
          loading="eager"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import UIButton from '@/views/components/ui/buttons/UIButton.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import UIRating from '@/views/components/ui/rating/UIRating.vue'
import heroImageSm from './images/hero/heroImage_sm.avif'
import heroImageMd from './images/hero/heroImage_md.avif'
import heroImageLg from './images/hero/heroImage_lg.avif'
import { useWindowSize } from '@vueuse/core'
import { computed } from 'vue'
import SectionTitle from '@/views/components/misc/SectionTitle.vue'

const features = [
  'Pass audits with clean evidence',
  'Answer client inquiries fast',
  'AI for sustainability professionals',
]

const { width } = useWindowSize()

const image = computed(() => {
  if (width.value < 768) {
    return heroImageSm
  }
  if (width.value < 1200) {
    return heroImageMd
  }

  return heroImageLg
})
</script>

<style lang="scss" scoped>
#SectionHero {
  position: relative;

  .SectionHero__container {
    width: 100%;
    max-width: calc(1280px + 4rem);
    margin: 0 auto;
    display: grid;
    padding: 2rem 1.25rem;

    @include md {
      padding: 2.5rem 2rem 2rem;
      grid-template-columns: 457px 1fr;
    }

    @include lg {
      grid-template-columns: 551px 1fr;
    }
  }

  .SectionHero__image-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .SectionHero__image {
    height: auto;
    position: relative;
    margin-top: 1.25rem;

    @include md {
      margin-top: 0;
      width: 420px;
      height: 420px;
      margin-left: -2rem;
    }

    @include lg {
      position: absolute;
      width: 829.182px;
      height: 500px;
      aspect-ratio: 829.18/500;
      left: -4rem;
      right: unset;
    }

    @include xl {
      left: 0;
    }
  }

  .SectionHero__title-highlight {
    background: linear-gradient(45deg, #023025 0%, #04785e 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  .SectionHero__features {
    margin-top: 1.25rem;
    padding-bottom: 0.5rem;
    display: grid;
    gap: 1.25rem;

    @include lg {
      margin-top: 2rem;
    }
  }

  .SectionHero__feature {
    @include font-size-16px();
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
    color: $color-ui-gray-cool-600;
    gap: 0.75rem;

    @include md {
      margin-left: 1rem;
    }

    @include lg {
      @include font-size-18px();
    }
  }

  .SectionHero__feature-icon {
    --size: 1.75rem;
  }

  .SectionHero__cta {
    margin-top: 1.25rem;
    display: flex;

    @include xl {
      margin-top: 2rem;
    }
  }

  .SectionHero__rating {
    margin-top: 1.25rem;
    display: flex;

    @include xl {
      margin-top: 2rem;
    }
  }
}
</style>
